import React, { useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { useEntityGraphContext } from '../Wrapper/EntityGraphProvider'
import SydSelect from '../../../commonDesign/SydSelect'
import Switch from '../../../molecules/Switch'
import SydLabel from '../../../commonDesign/SydLabel'
import { ICON_NAMES } from '../../../../constants'
import SydButton from '../../../commonDesign/Button'
import Icon from '../../../atoms/Icon'
import { nodeTypeListMap } from '../NodeLinkComponents/constants'

const useStyles = makeStyles({
  toggleButton: {
    position: 'absolute',
    top: 15,
    right: 15,
    zIndex: 101
  },
  innerContainer: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '500px',
    background: 'color-mix(in srgb, var(--theme-color-surface-d0) 70%, transparent)',
    borderLeft: 'var(--border-primary-thin)',
    padding: 'var(--padding-md)',
    boxShadow: 'var(--shadow-md)',
    backdropFilter: 'blur(10px)',
    zIndex: 100,
    transform: 'translate(100%, 0)',
    transition: 'transform 0.3s ease-in-out',
    overflowY: 'scroll',
    '&.open': {
      transform: 'translate(0, 0)'
    }
  }
})

export const Settings = () => {
  const classes = useStyles()
  const [displaySettings, setDisplaySetting] = useState(false)

  const { state, dispatch } = useEntityGraphContext()
  const { canvasSettings, nodeLinking, nodeCreation } = useMemo(() => state?.config ?? {}, [state])

  const nodeOptions = Object.entries(nodeTypeListMap).map(([key, value]) => ({
    value: key,
    label: value.label || key
  }))

  return (
    <div className={classes.container}>
      <SydButton className={classes.toggleButton} onClick={() => setDisplaySetting(!displaySettings)}>
        <Icon name={displaySettings ? ICON_NAMES.close : ICON_NAMES.settings} size='md' />
      </SydButton>
      <div className={clsx(classes.innerContainer, { open: displaySettings })}>
        <div className='flex flex-col gap-2'>
          <h2 className='text-lg font-bold m-0'>Settings</h2>
          <div className='flex flex-col gap-2'>
            <SydLabel label='User can add items'>
              <Switch
                checked={nodeCreation?.enabled}
                onChange={(e) => dispatch({
                  type: 'UPDATE_CONFIG',
                  payload: { path: ['nodeCreation', 'enabled'], value: e.target.checked }
                })}
              />
            </SydLabel>
          </div>
          {nodeCreation?.enabled && (
            <div className='flex flex-col gap-2'>
              <SydLabel label='Node Creation Types'>
                <SydSelect
                  multiple
                  size='sm'
                  value={nodeCreation?.levels ?? []}
                  onChange={(e) => dispatch({
                    type: 'UPDATE_CONFIG',
                    payload: { path: ['nodeCreation', 'levels'], value: e.target.value }
                  })}
                  options={nodeOptions}
                />
              </SydLabel>
            </div>
          )}
          <hr className='w-full' />
          <div className='flex flex-col gap-2'>
            <SydLabel label='User can link items to entities'>
              <Switch
                checked={nodeLinking?.enabled}
                onChange={(e) => dispatch({
                  type: 'UPDATE_CONFIG',
                  payload: { path: ['nodeLinking', 'enabled'], value: e.target.checked }
                })}
              />
            </SydLabel>
          </div>
          {nodeLinking?.enabled && (
            <div className='flex flex-col gap-2'>
              <SydLabel label='Node Linking Types'>
                <SydSelect
                  multiple
                  size='sm'
                  value={nodeLinking?.levels ?? []}
                  onChange={(e) => dispatch({
                    type: 'UPDATE_CONFIG',
                    payload: { path: ['nodeLinking', 'levels'], value: e.target.value }
                  })}
                  options={nodeOptions.filter(x => !['custom'].includes(x.value))}
                />
              </SydLabel>
            </div>
          )}
          <hr className='w-full' />
          <div className='flex flex-col gap-2'>
            <SydLabel label='Path Type'>
              <SydSelect
                size='sm'
                value={canvasSettings?.pathType}
                onChange={(e) => dispatch({
                  type: 'UPDATE_CONFIG',
                  payload: {
                    path: ['canvasSettings', 'pathType'],
                    value: e.target.value
                  }
                })}
                options={[
                  { value: 'bezier', label: 'Bezier' },
                  { value: 'simpleBezier', label: 'Simple Bezier' },
                  { value: 'smoothstep', label: 'Smooth Step' },
                  { value: 'straight', label: 'Straight' }
                ]}
              />
            </SydLabel>
          </div>
          <hr className='w-full' />
          <div className='flex flex-col gap-2'>
            <SydLabel label='Hide Handlers'>
              <Switch
                checked={canvasSettings?.hideHandlers}
                onChange={(e) => dispatch({
                  type: 'UPDATE_CONFIG',
                  payload: { path: ['canvasSettings', 'hideHandlers'], value: e.target.checked }
                })}
              />
            </SydLabel>
          </div>
          <div className='flex flex-col gap-2'>
            <SydLabel label='Allow Editing'>
              <Switch
                checked={canvasSettings?.nodesEditable}
                onChange={(e) => dispatch({
                  type: 'UPDATE_CONFIG',
                  payload: { path: ['canvasSettings', 'nodesEditable'], value: e.target.checked }
                })}
              />
            </SydLabel>
          </div>
        </div>
      </div>
    </div>
  )
}
